import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format, isThisWeek, isToday, parseISO } from "date-fns";
import { Building2, Phone, Search, X } from "lucide-react";
import { getAllMyTasks } from "@/api/tasks";
import { DashboardHero, type HeroKpi } from "@/components/dashboard/DashboardHero";
import { QuickAccessCard, type QuickAccessItem } from "@/components/dashboard/QuickAccessCard";
import { TodoCard } from "@/components/dashboard/TodoCard";
import { WeekLoadCard } from "@/components/dashboard/WeekLoadCard";
import { useAuthStore } from "@/stores/auth.store";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import type { TaskRead } from "@/api/types";

type Focus = "overdue" | "today" | "week";

const FOCUS_TITLES: Record<Focus, string> = {
  overdue: "Overdue",
  today: "Today",
  week: "This week",
};

/**
 * Personal, actionable dashboard, after the owner's nidria front (2026-09-29): an
 * indigo hero with the greeting and the key numbers, then what to do (overdue first,
 * today, this week), this week's load, quick access and what comes later. Reporting
 * widgets (KPIs, funnel, trends) live on the Analytics page; this surface is what the
 * sales op sees first thing in the morning.
 */
export default function DashboardPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const perms = usePermissions();
  const callCenterEnabled = useFeature("call_center");
  const enrichmentEnabled = useFeature("enrichment");
  const [focus, setFocus] = useState<Focus | null>(null);

  const firstName = user?.full_name?.trim().split(/\s+/)[0] || "there";

  // The user's open tasks across every project (the cross-project /me/tasks feed),
  // fetched once on mount and bucketed in place.
  const [tasks, setTasks] = useState<TaskRead[] | null>(null);
  const [tasksError, setTasksError] = useState(false);

  const loadTasks = useCallback(async () => {
    if (!slug || !perms.canViewTasks) {
      setTasks([]);
      return;
    }
    try {
      // Open-lane keys differ per project: filter to open client-side.
      const items = await getAllMyTasks();
      setTasks(items.filter((t) => !t.is_completed));
      setTasksError(false);
    } catch {
      setTasks([]);
      setTasksError(true);
    }
  }, [slug, perms.canViewTasks]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  // `is_overdue` is computed server-side; today / this week use due_at. Tasks due later
  // or without a date go to "Upcoming".
  const buckets = useMemo(() => {
    const overdue: TaskRead[] = [];
    const today: TaskRead[] = [];
    const week: TaskRead[] = [];
    const later: TaskRead[] = [];
    for (const t of tasks ?? []) {
      if (!t.due_at) {
        later.push(t);
        continue;
      }
      const due = parseISO(t.due_at);
      if (t.is_overdue) overdue.push(t);
      else if (isToday(due)) today.push(t);
      else if (isThisWeek(due, { weekStartsOn: 1 })) week.push(t);
      else later.push(t);
    }
    // Undated tasks sort last (their "9999" key falls after any real date).
    const byDue = (a: TaskRead, b: TaskRead) =>
      (a.due_at ?? "9999").localeCompare(b.due_at ?? "9999");
    return {
      overdue: overdue.sort(byDue),
      today: today.sort(byDue),
      week: week.sort(byDue),
      later: later.sort(byDue),
    };
  }, [tasks]);

  // The per-row project label only when the open set spans several projects.
  const multiProject = useMemo(
    () =>
      new Set((tasks ?? []).map((t) => (t as { project_slug?: string }).project_slug ?? ""))
        .size > 1,
    [tasks],
  );

  const todo = focus ? buckets[focus] : [...buckets.overdue, ...buckets.today, ...buckets.week];
  const waiting = buckets.overdue.length + buckets.today.length + buckets.week.length;

  const showFocus = (next: Focus) => {
    setFocus(next);
    document.getElementById("dashboard-todo")?.scrollIntoView({ behavior: "smooth" });
  };
  const kpis: HeroKpi[] = [
    { key: "overdue", value: buckets.overdue.length, label: "Overdue", urgent: true, onClick: () => showFocus("overdue") },
    { key: "today", value: buckets.today.length, label: "Today", onClick: () => showFocus("today") },
    { key: "week", value: buckets.week.length, label: "This week", onClick: () => showFocus("week") },
    { key: "open", value: (tasks ?? []).length, label: "Open tasks", onClick: () => navigate(`/projects/${slug}/tasks`) },
  ];
  // Overdue leads only while it burns; otherwise it closes the row.
  const orderedKpis = buckets.overdue.length > 0 ? kpis : [...kpis.slice(1), kpis[0]];

  const quick: QuickAccessItem[] = [
    ...(callCenterEnabled && perms.canAccessCallCenter
      ? [{
          to: `/projects/${slug}/call-center`,
          icon: <Phone className="h-4 w-4" />,
          tile: "bg-indigo-500/[0.08] text-indigo-600",
          title: "Call session",
          hint: "Start calling prospects",
        }]
      : []),
    ...(enrichmentEnabled && perms.canAccessEnrichment
      ? [{
          to: `/projects/${slug}/enrichment`,
          icon: <Search className="h-4 w-4" />,
          tile: "bg-emerald-500/[0.10] text-emerald-600",
          title: "Enrichment",
          hint: "Enrich contacts",
        }]
      : []),
    {
      to: `/projects/${slug}/companies`,
      icon: <Building2 className="h-4 w-4" />,
      tile: "bg-violet-500/[0.10] text-violet-600",
      title: "Companies",
      hint: "Browse the pipeline",
    },
  ];

  const subtitle = `${format(new Date(), "EEEE d MMMM")} · ${
    tasks === null
      ? "loading your tasks"
      : waiting === 0
        ? "nothing waits for you"
        : `${waiting} ${waiting === 1 ? "task awaits" : "tasks await"} you`
  }`;
  const empty =
    tasks === null
      ? { title: "Loading your tasks…", hint: "" }
      : tasksError
        ? { title: "Couldn't load your tasks", hint: "Try again in a moment." }
        : { title: "You're all clear", hint: "Nothing overdue, due today or this week." };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <DashboardHero greeting={`Bonjour ${firstName}`} subtitle={subtitle} kpis={orderedKpis} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {perms.canViewTasks && (
            <TodoCard
              id="dashboard-todo"
              title={focus ? FOCUS_TITLES[focus] : "To do"}
              tasks={todo}
              slug={slug}
              showProject={multiProject}
              empty={empty}
              aside={
                focus && (
                  <button
                    type="button"
                    onClick={() => setFocus(null)}
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-500/[0.08] px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-500/[0.14]"
                  >
                    {FOCUS_TITLES[focus]}
                    <X className="h-3 w-3" />
                  </button>
                )
              }
            />
          )}
          {perms.canViewTasks && <WeekLoadCard tasks={tasks ?? []} />}
        </div>

        <div className="space-y-6">
          <QuickAccessCard items={quick} />
          {perms.canViewTasks && (
            <TodoCard
              title="Upcoming"
              tasks={buckets.later}
              slug={slug}
              rows={5}
              showProject={multiProject}
              empty={{ title: "Nothing planned later", hint: "Tasks due after this week show here." }}
              aside={
                <Link
                  to={`/projects/${slug}/tasks`}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                >
                  All tasks →
                </Link>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
